import { useState, useCallback, useRef, useContext } from 'react';
import { ReplyMessageContext } from '../../../context/ReplyingMessage/ReplyingMessageContext';
import { TIMING_CONFIG } from '../constants/messageConstants';

/**
 * 🎯 Hook customizado para gerenciar todas as ações de mensagens
 * 
 * Centraliza toda a lógica de:
 * - Hover/Leave de mensagens
 * - Menu floating actions
 * - Ações de resposta e encaminhamento
 * - Duplo clique para abrir menu
 * - Estados de UI relacionados
 * 
 * @returns {Object} Objeto com handlers e estado das ações de mensagem
 */
const useMessageActions = () => {
  // 🎯 Estados do hook
  const [hoveredMessageId, setHoveredMessageId] = useState(null);
  const hoverTimeoutRef = useRef(null);
  
  // 🎯 Contextos
  const { setReplyingMessage } = useContext(ReplyMessageContext);

  // 🎯 Handler para hover de mensagem
  const handleMessageHover = useCallback((messageId) => {
    setHoveredMessageId(messageId);
    // Limpar timer se existir
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  }, []);

  // 🎯 Handler para deixar mensagem (mouse leave)
  const handleMessageLeave = useCallback(() => {
    // Usar timer para dar tempo de chegar no menu
    hoverTimeoutRef.current = setTimeout(() => {
      setHoveredMessageId(null);
    }, TIMING_CONFIG.HOVER_DELAY); // 300ms de delay
  }, []);

  // 🎯 Handler para mouse entrar no menu floating
  const handleMenuMouseEnter = useCallback(() => {
    // Cancelar timer quando mouse entra no menu
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  }, []);

  // 🎯 Handler para mouse sair do menu floating
  const handleMenuMouseLeave = useCallback(() => {
    // Esconder menu imediatamente quando sai do menu
    setHoveredMessageId(null);
  }, []);

  // 🎯 Handler para ação de resposta
  const handleFloatingReply = useCallback((message) => {
    setReplyingMessage(message);
  }, [setReplyingMessage]);

  // 🎯 Handler para ação de encaminhamento
  const handleFloatingForward = useCallback((message) => {
    // Implementar lógica de encaminhar
    console.log("Encaminhar mensagem:", message);
  }, []);

  // 🎯 Handler para duplo clique na mensagem (abre menu)
  const handleDoubleClickMessage = useCallback((message) => {
    // Força o FloatingMessageActions a aparecer
    setHoveredMessageId(message.id);
    
    // Cancela qualquer timer pendente para manter o menu visível
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
    
    // Aguarda a renderização do FloatingMessageActions e simula clique no "Mais opções"
    setTimeout(() => {
      const moreOptionsButton = document.querySelector(`[data-message-id="${message.id}"] button[data-action="more-options"]`);
      if (moreOptionsButton) {
        // Simula um clique real no botão
        moreOptionsButton.click();
      }
    }, TIMING_CONFIG.DOUBLE_CLICK_DELAY); // Pequeno delay para garantir renderização
  }, []);

  // 🧹 Cleanup function para limpar timers
  const cleanup = useCallback(() => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  }, []);

  // 🎯 Retorna todos os handlers e estado
  return {
    // Estados
    hoveredMessageId,
    
    // Handlers de hover/menu
    handleMessageHover,
    handleMessageLeave,
    handleMenuMouseEnter,
    handleMenuMouseLeave,
    
    // Handlers de ações
    handleFloatingReply,
    handleFloatingForward,
    handleDoubleClickMessage,
    
    // Utilitários
    cleanup,
  };
};

export default useMessageActions;
